import "@/App.css";
import { useEffect, useState } from "react";
import { BrowserRouter, Navigate, Route, Routes, useLocation } from "react-router-dom";
import ResetPassword from "./pages/ResetPassword";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { useAssetPreload } from "./hooks/useAssetPreload";
import { SplashScreen } from "./components/SplashScreen";
import { music } from "./lib/music";
import { SidePane } from "./components/SidePane";
import { RotateHint } from "./components/RotateHint";
import { TournamentCall } from "./components/TournamentCall";
import { SeatWaiting } from "./components/SeatWaiting";
import Landing from "./pages/Landing";
import Lobby from "./pages/Lobby";
import TablePage from "./pages/TablePage";
import VerifyPage from "./pages/VerifyPage";
import RulesPage from "./pages/RulesPage";
import { PrivacyPage, TermsPage } from "./pages/LegalPage";
import ContactPage from "./pages/ContactPage";
import LeaderboardPage from "./pages/LeaderboardPage";
import RegionBlocked from "./pages/RegionBlocked";
import AdminPage from "./pages/AdminPage";
import AffiliatePage from "./pages/AffiliatePage";
import ClubRoomPage from "./pages/ClubRoomPage";
import ClubAdminPage from "./pages/ClubAdminPage";
import { AdminLogin } from "./components/admin/AdminLogin";
import { C } from "./lib/api";
import { ErrorBoundary, reportClientError } from "./components/ErrorBoundary";
import { VersionWatch } from "./components/VersionWatch";
import { GuestJoinPrompt, JoinCelebrationHost } from "./components/JoinDialog";

const TITLES = [[/^\/admin/, "ADMIN"], [/^\/lobby/, "Lobby · Players Room"], [/^\/table/, "Table · Players Room"],
  [/^\/rules/, "Rules · Players Room"], [/^\/verify/, "Verify · Players Room"], [/^\/leaderboard/, "Leaderboard · Players Room"],
  [/^\/privacy/, "Privacy Policy · Players Room"], [/^\/terms/, "Terms and Conditions · Players Room"]];

// browser tab text per screen (/admin reads simply "ADMIN")
const TitleSync = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    document.title = (TITLES.find(([re]) => re.test(pathname)) || [, "Players Room"])[1];
  }, [pathname]);
  return null;
};

// /admin has its own staff sign-in: no session or a player session shows the staff login instead of the game's landing page
const AdminGate = () => {
  const { user } = useAuth();
  useEffect(() => { music.stop(); }, []); // control room: no background music
  if (user === null) return <SplashScreen pct={100} />;
  if (!user || !C.STAFF_ROLES.includes(user.role)) return <AdminLogin current={user || null} />;
  return <AdminPage />; // no player side menu in the control room: nothing here navigates back into the game
};

const Protected = ({ children }) => {
  const { user } = useAuth();
  if (user === null) return <SplashScreen pct={100} />;
  if (user === false) return <Navigate to="/" replace />;
  return (
    <>
      <SidePane />
      <GuestJoinPrompt />
      {children}
    </>
  );
};

const Public = ({ children }) => {
  const { user } = useAuth();
  if (user) return <Navigate to="/lobby" replace />;
  return children;
};

// Splash until the session check resolves and the art is in cache (ring shows real preload progress).
// Held for SPLASH_MS on a cold load — the title card is the first impression, let it land.
const SPLASH_MS = 7000;
const FAST_BOOT = (() => {  // a crash sent us back here: no 7-second title card, the player wants their seat
  const fast = sessionStorage.getItem("pfp_fast_boot") === "1";
  sessionStorage.removeItem("pfp_fast_boot");
  return fast;
})();
// The API can't be reached (restarting, or the player's connection dropped). Say so and keep retrying — never bounce
// a seated player out to the sign-in page, and never show a bare black screen.
const Unreachable = () => (
  <div data-testid="app-offline" className="min-h-screen bg-slate-950 flex items-center justify-center p-6">
    <div className="gold-card rounded-2xl p-7 max-w-md w-full flex flex-col items-center gap-3 text-center">
      <div className="font-display text-lg text-amber-50">Reconnecting to Players Room…</div>
      <p className="text-sm text-slate-300">The room is briefly unreachable. Your seat, your chips and any hand in progress are held on the server — this will come back on its own.</p>
      <button type="button" data-testid="offline-retry" onClick={() => window.location.reload()} className="btn-gold rounded-full px-4 py-2 text-xs font-display tracking-wider btn-lift">TRY AGAIN NOW</button>
    </div>
  </div>
);

const Boot = ({ children }) => {
  const { user, offline } = useAuth();
  const pct = useAssetPreload();
  const [elapsed, setElapsed] = useState(0);
  const hold = FAST_BOOT ? 0 : SPLASH_MS;
  useEffect(() => {
    const t0 = Date.now();
    const iv = setInterval(() => setElapsed(Date.now() - t0), 100);
    return () => clearInterval(iv);
  }, []);
  useEffect(() => { music.start(1, { ifIdle: true }); }, []); // loading page (browsers let it start on the first click); child screens' effects run first and win
  const minShown = elapsed >= hold;
  // the ring never races ahead of the hold timer — it keeps "loading" until the splash is actually about to lift
  const shown = hold ? Math.min(pct, Math.floor((elapsed / hold) * 100)) : pct;
  if (offline && user === null) return <Unreachable />;
  if (user === null || pct < 100 || !minShown) return <SplashScreen pct={Math.min(shown, 99)} />;
  return children;
};

// Anything that escapes React (async handlers, promises) is reported too, so the log shows what happened
// even when the screen survives it.
if (typeof window !== "undefined" && !window.__pfpErrHooked) {
  window.__pfpErrHooked = true;
  window.addEventListener("error", (e) => reportClientError("window", e.error || e.message, { at: `${e.filename}:${e.lineno}` }));
  window.addEventListener("unhandledrejection", (e) => reportClientError("promise", e.reason));
}

// Deliberate crash, used to prove the guard above still catches a broken screen after a deploy (/crashtest?go=1).
const CrashTest = () => {
  if (new URLSearchParams(window.location.search).get("go") !== "1") return <Navigate to="/lobby" replace />;
  throw new Error("crash test");
};

export default function App() {
  return (
    <div className="App">
      <ErrorBoundary>
      <AuthProvider>
        <BrowserRouter>
          <TitleSync />
          <Boot>
            <RotateHint />
            <VersionWatch />
            <JoinCelebrationHost />
            <Routes>
              <Route
                path="/"
                element={
                  <Public>
                    <Landing />
                  </Public>
                }
              />
              <Route path="/crashtest" element={<CrashTest />} />
              <Route path="/affiliate" element={<AffiliatePage />} />
              <Route path="/blocked" element={<RegionBlocked />} />
              <Route
                path="/lobby"
                element={
                  <Protected>
                    <Lobby />
                  </Protected>
                }
              />
              <Route
                path="/table/:tableId"
                element={
                  <Protected>
                    <TablePage />
                  </Protected>
                }
              />
              <Route
                path="/verify"
                element={
                  <Protected>
                    <VerifyPage />
                  </Protected>
                }
              />
              <Route
                path="/rules"
                element={
                  <Protected>
                    <RulesPage />
                  </Protected>
                }
              />
              <Route path="/leaderboard" element={<Protected><LeaderboardPage /></Protected>} />
              <Route path="/reset" element={<ResetPassword />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/admin" element={<AdminGate />} />
              {/* Club rooms are public so a club can share its own link; their dashboard is not. */}
              <Route path="/:slug/admin" element={<ClubAdminPage />} />
              <Route path="/:slug" element={<ClubRoomPage />} />
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
            <TournamentCall />
            <SeatWaiting />
            <Toaster theme="dark" position="top-center" richColors />
          </Boot>
        </BrowserRouter>
      </AuthProvider>
      </ErrorBoundary>
    </div>
  );
}
